/**
 * WHICH ACCOUNTING SYSTEM KEEPS THIS COMPANY'S BOOKS — shown and saved FROM THE COMPANY SCREEN.
 * ============================================================================================
 *
 * **Owner finding 6, 2026-10-20**, and his decision of 2026-10-21:
 * *"בחירת מערכת הנהלת חשבונות"* is one of five fields the company-management chapter makes
 * mandatory to activate a company, and *"הבחירה תוצג ותישמר ממסך החברה, בלי מזהה ספק מומצא ובלי
 * להציג ״מחובר״ לפני בדיקה. הרשימה תבחין בין מערכות הנהלת חשבונות לבין ספקי בנקאות ומקורות
 * אחרים."*
 *
 * Until this component the field had nowhere to be. `CompanySettingsPanel` could only report it as
 * `absent` — the schema had no place for a selection made before the company's code in that system
 * was known — and the one surface that touched source systems was the CONNECTION screen, which
 * needs a live connection and mints an external identifier. Migration `0020` added the dedicated
 * column; this is the control over it.
 *
 * ── THE WORD "מחובר" DOES NOT APPEAR IN THIS FILE, AND THAT IS THE REQUIREMENT ──────────────────
 *
 * A selection says WHERE THE BOOKS ARE KEPT. It says nothing about whether anything was reached,
 * and this component is given nothing it could claim that with: `companies.source_system.read`
 * carries a selection and a list, and no status, no freshness and no last-checked.
 *
 * That is deliberate twice over. 07 N-5 is the scar — a missing `data_source_type` became a working
 * default on 5 of 313 companies, so five unconfigured companies behaved exactly like five
 * configured ones — and `SourceAdminService`'s own header records that the store which would answer
 * "is this reachable" is NOT IMPLEMENTED. A component that rendered a green tick here would be
 * stating something no part of this build can measure.
 *
 * So the lead sentence says what the selection means and what it does not, in the Hebrew an
 * operator reads, rather than relying on the absence of a tick to communicate it.
 *
 * ── THE LIST IS THE SERVER'S, AND IT IS ALREADY FILTERED ────────────────────────────────────────
 *
 * `selectable` holds only ACTIVE ACCOUNTING systems: the server reads
 * `core.external_system.system_role = 'accounting'`, so a banking provider or a legacy key space
 * cannot appear here even if someone adds one tomorrow. This component invents no vocabulary, maps
 * no codes to names, and has no list of its own to fall out of date.
 *
 * `selected.isSelectable === false` means the company points at a system that has since been
 * retired or reclassified. It is SHOWN, with that fact stated — hiding it would render a stale
 * selection as *"טרם הוגדר"*, and nobody would know that company needs re-pointing.
 */

import { useCallback, useState, type JSX } from 'react';

import { ActionFailureNotice } from './ActionFailureNotice';
import { CataloguePicker, type PickerOption } from './CataloguePicker';
import { InfoHint } from './InfoHint';
import { StateBlock } from './StateBlock';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';

export interface CompanyAccountingSystemProps {
  /** The company this is about. It travels in the ADDRESS; there is no body field for it. */
  readonly companyUid: string;
}

type Saved =
  | { readonly kind: 'set'; readonly nameHe: string }
  | { readonly kind: 'unchanged' }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

export function CompanyAccountingSystem({ companyUid }: CompanyAccountingSystemProps): JSX.Element {
  /*
   * `useCallback` because `useServerData` has the reader in its effect's dependency list: a new
   * function identity on every render is a fetch on every render. Keyed on the company, so moving
   * between companies re-reads and does not show the previous one's selection.
   */
  const read = useCallback(
    () => callOperation('companies.source_system.read', { params: { company_uid: companyUid } }),
    [companyUid],
  );
  const current = useServerData(read);

  const [choice, setChoice] = useState('');
  const [sending, setSending] = useState(false);
  const [saved, setSaved] = useState<Saved | null>(null);

  if (current.result.state === 'loading') {
    return (
      <StateBlock
        titleHe="מערכת הנהלת חשבונות"
        bodyHe="קורא את הבחירה הקיימת ואת רשימת המערכות."
      />
    );
  }
  if (current.result.state === 'failed') {
    /*
     * A FAILED READ IS NOT AN EMPTY SELECTION. Rendering the picker with no current value would
     * invite an operator to "fix" a field whose real value nobody managed to read -- and the save
     * would then overwrite it.
     */
    return (
      <section className="tile">
        <h3 className="breakdown__title">מערכת הנהלת חשבונות</h3>
        <ActionFailureNotice failure={current.result.failure} />
      </section>
    );
  }

  const { selected, selectable } = current.result.data;

  const options: readonly PickerOption[] = selectable.map((system) => ({
    value: system.systemCode,
    labelHe: system.displayName,
    // The code is a MUTED aside, so an operator who knows `priority` can type it and one who does
    // not never has to: *"אין לדרוש הקלדת קודים מהזיכרון"*.
    detail: system.systemCode,
  }));

  function save(): void {
    if (choice === '') return;
    setSending(true);
    setSaved(null);
    void callOperation('companies.source_system.select', {
      params: { company_uid: companyUid },
      body: { systemCode: choice },
    })
      .then(
        (answer) => {
          setSaved(
            answer.outcome === 'unchanged'
              ? { kind: 'unchanged' }
              : {
                  kind: 'set',
                  // THE NAME THE SERVER ECHOED BACK, not the one this screen had in its list --
                  // *"לאחר שמירה יש להציג את השיוך שנשמר"*. If they ever disagree, the stored one
                  // is the truth and this is the one that shows.
                  nameHe: answer.selected?.displayName ?? choice,
                },
          );
          setChoice('');
          current.reload();
        },
        (error: unknown) => setSaved({ kind: 'failed', failure: classifyActionError(error) }),
      )
      .finally(() => setSending(false));
  }

  return (
    <section className="tile">
      <h3 className="breakdown__title">מערכת הנהלת חשבונות</h3>

      {/*
        WHAT THE FIELD MEANS, AND WHAT IT DOES NOT. The second sentence is the Owner's constraint
        rendered rather than merely obeyed: nothing on this screen may read as a connection.
      */}
      {/*
        ⓘ, AND ONLY HERE. **2026-10-28.** The paragraph that stood here said four things, and three
        of them were about what this control is NOT. One of them a reader cannot act without: a
        selection here is a DECLARATION of where the books are kept, and not a connection — which is
        the difference between this tile and the connections screen, and the thing somebody will get
        wrong. So that one fact is behind the mark, and the other three are gone.
      */}
      <InfoHint aboutHe={'מערכת הנהלת חשבונות'}>
        <p>הצהרה על מקום הספרים. אינה חיבור — חיבור נעשה במסך החיבורים.</p>
      </InfoHint>

      {selected === null ? (
        <p className="field__hint">טרם נבחרה</p>
      ) : (
        <p className="breakdown__note">
          {`נבחרה: ${selected.displayName}`}
          {/* THE FACT, not the paragraph: the stored selection stands either way. */}
          {selected.isSelectable ? '' : ' · אינה מוצעת עוד לבחירה'}
        </p>
      )}

      <CataloguePicker
        name="sourceSystemCode"
        labelHe={selected === null ? 'בחירת המערכת' : 'החלפת המערכת'}
        options={options}
        value={choice}
        onChange={setChoice}
        disabled={sending}
        placeholderHe="חיפוש לפי שם המערכת"
        emptyHe="לא הוגדרו מערכות הנהלת חשבונות בהתקנה הזאת. הרשימה נקראת מהקטלוג ואינה מוקלדת כאן."
        hintHe="הרשימה כוללת מערכות הנהלת חשבונות פעילות בלבד, ואינה כוללת ספקי בנקאות או מקורות אחרים."
      />

      <div className="actions-row">
        <button
          type="button"
          className="btn btn--primary"
          disabled={sending || choice === '' || choice === selected?.systemCode}
          onClick={save}
        >
          {sending ? 'שומר…' : 'שמירת הבחירה'}
        </button>
      </div>

      {saved === null ? null : saved.kind === 'set' ? (
        <StateBlock
          titleHe="הבחירה נשמרה"
          bodyHe={`הנהלת החשבונות של החברה מנוהלת ב${saved.nameHe}. לא נוצר חיבור ולא נבדקה נגישות.`}
        />
      ) : saved.kind === 'unchanged' ? (
        <StateBlock
          titleHe="לא היה שינוי"
          bodyHe="המערכת שנבחרה היא זו שהייתה רשומה כבר, ולכן לא נכתב דבר."
        />
      ) : (
        <ActionFailureNotice failure={saved.failure} />
      )}
    </section>
  );
}
